<template>
    <div>
        <v-card flat
                v-if="readonly"
                color="rgb(0,0,0,0)"
        >
            <v-card-title class="pa-0 mb-3">방문 정보</v-card-title>
        </v-card>

        <v-card v-else class="mb-6 pa-3 pa-md-6 visit-info-card">
            <v-card-title class="pa-0 mb-3">방문 정보</v-card-title>
            <validation-provider rules="required" v-slot="{ errors }" name="방문목적">
                <v-select
                    v-model="innerInfo.purpose"
                    :items="purposes"
                    label="방문목적 *"
                    :error-messages="errors[0]"
                ></v-select>
            </validation-provider>

            <validation-provider rules="required" v-slot="{ errors }" name="방문 예정일">
                <v-text-field
                    :value="dateTimeText"
                    append-icon="mdi-calendar-month"
                    readonly
                    label="방문 예정일 *"
                    :error-messages="errors[0]"
                    @click="menu = true"
                />
            </validation-provider>

            <v-dialog
                v-model="menu"
                max-width="350"
                persistent
                content-class="rounded-lg"
            >
                <v-card>
                    <v-card-text class="pa-4">
                        <v-date-picker
                            v-model="innerInfo.startDate"
                            no-title
                            full-width
                            locale="ko-KR"
                            color="var(--primary-color, #2196f3)"
                            :day-format="d => new Date(d).getDate()"
                            :min="new Date().toISOString().substr(0, 10)"
                            :max="maxDate"
                        ></v-date-picker>

                        <v-row align="center" dense>
                            <v-col cols="auto">
                                <v-icon size="20">mdi-clock-outline</v-icon>
                            </v-col>
                            <v-col>
                                <v-select
                                    v-model="startTimeAmPm"
                                    :items="timeOptions.amPm"
                                    hide-details="auto"
                                    class="mt-0 pt-0"
                                ></v-select>
                            </v-col>
                            <v-col>
                                <v-select
                                    v-model="startTimeHour"
                                    :items="timeOptions.hour"
                                    item-text="text"
                                    item-value="value"
                                    hide-details="auto"
                                    class="mt-0 pt-0"
                                ></v-select>
                            </v-col>
                            <v-col>
                                <v-select
                                    v-model="startTimeMinute"
                                    :items="timeOptions.minute"
                                    item-text="text"
                                    item-value="value"
                                    hide-details="auto"
                                    class="mt-0 pt-0"
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-footer outlined
                              height="50"
                              color="#fff"
                              class="px-2 justify-center"
                    >
                        <v-row justify="end" align="center" no-gutters>
                            <v-col cols="auto">
                                <v-btn
                                    depressed
                                    width="100"
                                    class="ml-2 primary-btn"
                                    @click="closeDialog"
                                >
                                    확인
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-footer>
                </v-card>
            </v-dialog>

            <template v-if="companies.length === 1">
                <validation-provider rules="required" v-slot="{ errors }" name="방문할 지역">
                    <v-select
                        v-model="innerInfo.area"
                        :items="areas"
                        :item-text="item => innerInfo.company.companyName + ' ' + item.areaName"
                        item-value="areaId"
                        @change="changeArea"
                        required
                        no-data-text="데이터가 없습니다"
                        label="방문할 지역"
                        :readonly="areas.length === 1"
                        :error-messages="errors[0]"
                    ></v-select>
                </validation-provider>
            </template>

            <template v-if="companies.length !== 1">
                <validation-provider rules="required" v-slot="{ errors }" name="방문할 회사">
                    <v-select
                        v-model="innerInfo.company"
                        :items="companies"
                        item-text="companyName"
                        item-value="companyId"
                        @change="changeCompany"
                        return-object
                        no-data-text="데이터가 없습니다"
                        label="방문할 회사"
                        :error-messages="errors[0]"
                    >
                    </v-select>
                </validation-provider>
                <validation-provider rules="required" v-slot="{ errors }" name="방문할 지역">
                    <v-select
                        v-model="innerInfo.area"
                        :items="areas"
                        item-text="areaName"
                        item-value="areaId"
                        @change="changeArea"
                        required
                        no-data-text="데이터가 없습니다"
                        label="방문할 지역"
                        :error-messages="errors[0]"
                    ></v-select>
                </validation-provider>
            </template>

            <validation-provider rules="required" v-slot="{ errors }" name="접견 대상자">
                <v-autocomplete
                    v-model="innerInfo.targetUser"
                    :items="userEntries"
                    item-text="userName"
                    item-value="userId"
                    :loading="isLoading"
                    :search-input.sync="search"
                    clearable
                    no-filter
                    return-object
                    :menu-props="menuProps"
                    label="접견 대상자"
                    :no-data-text="isLoading ? '검색 중...' : '선택할 대상이 없습니다'"
                    placeholder="검색할 이름을 입력하세요"
                    :error-messages="errors[0]"
                >
                    <template v-slot:selection="{ attr, on, item }">
                        {{ item.userName }} {{ item.titleName }} ({{ item.deptName }})
                    </template>

                    <template v-slot:item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>{{ item.userName }} {{ item.titleName }} ({{ item.deptName }})</v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-autocomplete>
            </validation-provider>
        </v-card>
    </div>
</template>

<script>
import Debounce from "@/service/debounce";
import {extend, ValidationProvider} from 'vee-validate';
import {required} from 'vee-validate/dist/rules';
import cryptoWrapper from "@/service/cryptoWrapper";
import {mapState} from "vuex";

extend('required', {
    ...required,
    message: '{_field_}을 입력해주세요.',
})

const INTERVAL = 10;

export default {
    name: 'ApplyStep2',

    props: {
        value: Object,
        readonly: {
            type: Boolean,
            default: false,
            required: false,
        },
    },

    components: {
        ValidationProvider
    },

    data: () => ({
        isLoading: false,
        companies: [],
        areas: [],
        purposes: [],
        searchDebounce: new Debounce(),
        userEntries: [],
        search: null,
        menu: false,
        locale: {
            type: String,
            default: 'ko-kr',
        },

        innerInfo: {
            purpose: null,
            companies: [],
            areas: [],
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            company: null,
            area: null,
            targetUser: null,
        },
        menuProps: {
            maxHeight: 150,
            transition: false,
            offsetY: true,
            openOnClick:false,
            closeOnContentClick: false,
        },

        startTimeHour: null,
        startTimeMinute: null,
        startTimeAmPm: null
    }),

    computed: {
         ...mapState(['secretKey', 'iv']),

        timeOptions() {
             const amPm = [ '오전', '오후' ];
             const hour = [];
             const minute = [];

             for (let i = 1; i <= 12; i++) {
                 hour.push({
                     text: i.toString().padStart(2, '0') + '시',
                     value: i.toString().padStart(2, '0'),
                 });
             }

             for (let i = 0; i < 60 / INTERVAL; i++) {
                 minute.push({
                     text: (i * INTERVAL).toString().padStart(2, '0') + '분',
                     value: (i * INTERVAL).toString().padStart(2, '0')
                 });
             }

             return { amPm, hour, minute };
        },

        dateTimeText() {
             return this.innerInfo.startDate + ' ' + this.startTimeAmPm + ' ' + this.startTimeHour + ':' + this.startTimeMinute;
        },

        maxDate() {
            const educationDay = this.$code.get('service.education.education_day');
            return this.$moment().add(educationDay, 'days').format("YYYY-MM-DD");
        },
    },

    watch: {
        innerInfo: {
            async handler(v) {
                this.$emit('input', v);
            },
            deep: true
        },

        /**
         * 사용자 검색
         * (500ms 이내 키 입력 중이면 검색하지 않음)
         *
         * @param name
         */
        search(name) {
            const companyId = this.innerInfo.company?.companyId;
            const self = this;


            const searchUser = function(userName) {
                if (!userName || self.isLoading) return;

                self.isLoading = true;
                self.$axios.get('/v1/home/user/searchByName', {
                    params: {companyId: companyId, name: userName}
                }).then(res => {
                    self.userEntries = [...res.data];
                    self.userEntries.forEach((user) => {
                        user.alias = `${user.userName} (${user.deptName})`;
                        user.originName = cryptoWrapper.decrypt(user.encryptedName, self.secretKey, self.iv);
                    });
                }).catch(err => {
                    console.error(err);
                    if (err?.response?.data?.message) {
                        self.$window.alert(err?.response?.data?.message, 'error');
                    } else {
                        self.$window.alert(`접견 대상자를 검색할 수 없습니다.\n${err.message}`, 'error');
                    }
                }).finally(() => {
                    self.isLoading = false;
                })
            }

            this.searchDebounce.delay(() => {
                searchUser(name);
            }, 500);
        },

        'innerInfo.company': function(v) {
            this.fetchArea(v);
        },
    },

    async created() {
        await this.init();
    },

    mounted() {
        this.$nextTick(() => {
            this.search = this.innerInfo.targetUser?.userName;
        })
    },

    methods: {
        async init() {
            this.isLoading = true;
            this.innerInfo = {...this.value};
            try {
                this.initTime();
                await this.fetchCompanies();
                await this.fetchPurposes();
            } catch(err) {
                console.error(err);
                this.$window.alert(err, 'error');
            } finally {
                this.isLoading = false;
            }
        },

        async fetchCompanies() {
            const result = await this.$axios.get('/v1/home/company/findAll');

            this.companies = [ ... result.data ];
            if (this.companies.length === 1) {
                this.innerInfo.company = this.companies[0];
            }
        },

        async fetchArea(company) {
            const result = await this.$axios.get(`/v1/home/area/findAll/${company.companyId}`);

            this.areas = [ ... result.data ].filter(e => e.useYn === 'Y');
            if (this.areas.length === 1) {
                this.innerInfo.area = this.areas[0];
            }
        },

        fetchPurposes() {
            const purposes = this.$code.get("service.purposes");
            this.purposes = JSON.parse(purposes);
        },

        changeCompany(company) {
            this.innerInfo.company = this.companies.find(c => c.companyId === company.companyId);
            this.innerInfo.area = null;
        },

        changeArea(areaId) {
            this.innerInfo.area = this.areas.find(area => area.areaId === areaId);
        },

        initTime() {
            const now = this.$moment();
            const roundedMinutes = Math.ceil(now.minutes() / INTERVAL) * INTERVAL;
            if (roundedMinutes === 60) {
                now.add(1, 'hours').startOf('hour');
            } else {
                now.minutes(roundedMinutes);
            }

            this.innerInfo.startTime = this.innerInfo.startTime
                ? this.innerInfo.startTime
                : now.format('HH:mm');

            this.startTimeAmPm = now.hour() < 12 ? '오전' : '오후';
            this.startTimeHour = this.timeOptions.hour.find(e => e.value === now.format('hh')).value;
            this.startTimeMinute = this.timeOptions.minute.find(e => e.value === now.format('mm')).value;
            this.innerInfo.startDate = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
        },

        setStartTime() {
            const amPm = this.startTimeAmPm;
            let hour = this.startTimeHour;
            const minute = this.startTimeMinute;

            if (amPm === '오전') {
                hour = hour === '12' ? '00' : hour;
            } else {
                hour = hour === '12' ? '12' : String(Number(hour) + 12);
            }
            this.innerInfo.startTime = `${hour}:${minute}`;
        },

        closeDialog() {
            this.setStartTime();
            this.menu = false;
        }
    }
}
</script>

<style scoped>
</style>